import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { MaintenanceComponent } from './domain/login/component/maintenance/maintenance.component';
import { AuthGuard } from './util/guards/login.guard';
import { CabinetComponent } from './domain/cabinet/component/cabinet/cabinet.component';
import { EmptyState404Component } from './domain/empty-state/empty-state-404/empty-state-404.component';
import { ComponentPageComponent } from './domain/components-page/component/component-page.component';
import { CommitmentAllocationModelComponent } from './domain/follow-on/component/commitment-allocation-model/commitment-allocation-model.component';
import { HoldingComponent } from './domain/holding/component/holding/holding.component';
import { FundStrategyComponent } from './domain/fund-strategy/component/fund-strategy.component';
import { PP_URLS_MARKET } from './domain/market-analysis/module/market-analysis-routing.module';
import { UnsavedChangesGuard } from './domain/shared/component/can-deactivate-guard/can-deativate-guard';
import { KPI_URLS } from './domain/kpi/module/kpi-module-routing.module';
import { OrgUserManagementComponent } from "./domain/org-user-management/org-user-management.component";
import { FP_URLS } from './domain/fund-performance/component/fund-performances/pojo/FP_URLS';
import { FS_URLS } from './domain/financial-statement/module/financial-statement-routing.module';
import { ORG_URLS } from "./domain/organization/dto/ORG_URLS";
import { EntranceUrl } from "./domain/entrance/service/entrance.service";
import { INVESTORS_URL } from './domain/investors-and-net-irr/dto/InvestorsURL';
import { CP_URLS } from './domain/company-performance/dto/CP_URLS';
import { FundBudgetModelComponent } from "./domain/fund-budget-model/components/main/fund-budget-model.component";
import { BUDGET_MODEL_URL } from "./domain/fund-budget-model/pojo/bm-const";

const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'login' },
  {
    path: 'login',
    loadChildren: () => import('./domain/login/module/login.module')
      .then((m) => m.LoginModule),
  },
  { path: 'maintenance', component: MaintenanceComponent },
  {
    path: FP_URLS.BASE,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        'src/app/domain/fund-performance/module/fund-performance.module'
      ).then((m) => m.FundPerformanceModule),
  },
  {
    path: 'periods-comparison',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        'src/app/domain/portfolio-comparison/module/portfolio-comparison.module'
      ).then((m) => m.PortfolioComparisonModule),
  },
  {
    path: 'cabinet',
    canActivate: [AuthGuard],
    component: CabinetComponent,
    loadChildren: () =>
      import(
        'src/app/domain/cabinet/module/Cabinet.module'
      ).then((m) => m.CabinetModule),
  },
  {
    path: 'org-user-management',
    canActivate: [AuthGuard],
    component: OrgUserManagementComponent,
    loadChildren: () =>
      import(
        'src/app/domain/org-user-management/module/org-user-management.module'
      ).then((m) => m.OrgUserManagementModule),
  },
  {
    path: 'recovery',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        'src/app/domain/recovery/module/recovery-module'
      ).then((m) => m.RecoveryModule),
  },
  {
    path: 'portfolio-performance',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('src/app/domain/performance-prediction-model/module/performace-prediction-model.module').then(
        (m) => m.PerformancePredictionModule,
      ),
  },
  {
    path: ORG_URLS.BASE,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        'src/app/domain/organization/module/organization.module'
      ).then((m) => m.OrganizationModule),
  },
  {
    path: 'portfoliocompanies',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        'src/app/domain/company/module/company-module'
      ).then((m) => m.CompanyModule),
  },

  {
    path: 'next-round-allocation',
    canActivate: [AuthGuard],
    component: HoldingComponent,
    loadChildren: () =>
      import('./domain/holding/module/holding.module').then((m) => m.HoldingModule),
  },
  {
    path: 'fund-strategy',
    canActivate: [AuthGuard],
    component: FundStrategyComponent,
    canDeactivate: [UnsavedChangesGuard],
    loadChildren: () =>
      import('./domain/fund-strategy/module/fund-strategy.module').then((m) => m.FundStrategyModule),
  },
  {
    path: BUDGET_MODEL_URL.base,
    canActivate: [AuthGuard],
    component: FundBudgetModelComponent,
    canDeactivate: [UnsavedChangesGuard],
  },
  {
    path: 'growth-potential',
    canActivate: [AuthGuard],
    component: CommitmentAllocationModelComponent,
    canDeactivate: [UnsavedChangesGuard],
    loadChildren: () =>
      import(
        'src/app/domain/follow-on/module/follow-on-model.module'
      ).then((m) => m.FollowOnModelModule),
  },
  {
    path: PP_URLS_MARKET.base,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('src/app/domain/market-analysis/module/market-analysis.module').then(
        (m) => m.MarketAnalysisModule,
      ),
  },
  {
    path: 'investor-report',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('src/app/domain/investor-report/module/investor-report.module').then(
        (m) => m.InvestorReportModule,
      ),
  },
  {
    path: 'published-investor-report',
    loadChildren: () =>
      import('src/app/domain/investor-report/module/published-investor-report.module').then(
        (m) => m.PublishedInvestorReportModule,
      ),
  },
  {
    path: FS_URLS.base,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        'src/app/domain/financial-statement/module/financial-statement.module'
      ).then((m) => m.FinancialStatementModule),
  },
  {
    path: 'components',
    canActivate: [AuthGuard],
    component: ComponentPageComponent,
    loadChildren: () =>
      import('src/app/domain/components-page/module/component.module').then(
        (m) => m.ComponentModule,
      ),
  },
  {
    path: 'documents',
    loadChildren: () =>
      import('src/app/domain/box/module/box.module').then((m) => m.BoxModule),
  },
  {
    path: 'onboarding',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('src/app/domain/onboarding/module/onboarding.module').then((m) => m.OnboardingModule),
  },
  {
    path: EntranceUrl,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('src/app/domain/entrance/module/entrance.module').then((m) => m.EntranceModule),
  },
  {
    path: KPI_URLS.base,
    loadChildren: () =>
      import('src/app/domain/kpi/module/kpi.module').then((m) => m.KpiModule),
  },
  {
    path: INVESTORS_URL.BASE,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('src/app/domain/investors-and-net-irr/investors-and-net-irr.module').then((m) => m.InvestorsAndNetIrrModule),
  },
  {
    path: CP_URLS.BASE,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        'src/app/domain/company-performance/module/company-performance.module'
      ).then((m) => m.CompanyPerformanceModule),
  },
  { path: '**', component: EmptyState404Component },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
  providers: [AuthGuard],
})
export class AppRoutingModule { }
